import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import Header from "../../components/layout/Header";
import Sidebar from "../../components/layout/Sidebar";
import { Helmet } from "react-helmet";
import { CDN_BASE_URL } from "../../config";

export default function GradingDetailPage() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const { t, i18n } = useTranslation();

  const [head, setHead] = useState(null); // meta + totals
  const [rows, setRows] = useState([]); // فقط needsReview
  const [saving, setSaving] = useState(false);

  const fetchAttempt = async () => {
    const res = await httpRequest(
      "GET",
      `/attempt/${attemptId}`,
      {},
      { "x-access-token": user.token }
    );
    const data = res?.data?.data ?? null;
    setHead(data);

    // ✅ فقط آیتم‌هایی که نیاز به تصحیح دارند
    const all = data?.responses ?? [];
    setRows(all);
  };

  useEffect(() => {
    if (!user?.token) return;
    fetchAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  const setRowPatch = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      // فقط ردیف‌هایی که نمایش می‌دیم (already filtered) را می‌فرستیم
      const updates = rows
        .filter((r) => r.needsReview === true) // فقط آن‌هایی که مصحح حق دست‌کاری دارد
        .map((r) => ({
          index: r.index,
          manualScore: Number(r.manualScore || 0),
          comment: r.comment || "",
        }));

      const res = await httpRequest(
        "POST",
        "/attempt/manual-grade",
        { attemptId, updates },
        { "x-access-token": user.token }
      );

      // refresh
      await fetchAttempt();

      if (res?.data?.data?.status === "scored") {
        navigate("/organization/grading");
      }
    } finally {
      setSaving(false);
    }
  };

  const gradeRowWithAI = async (idx, r) => {
    try {
      setRowPatch(idx, { aiLoading: true, aiError: null });

      const res = await httpRequest(
        "POST",
        "/ai/ai-grade-one",
        {
          attemptId,
          index: r.index,
        },
        { "x-access-token": user.token }
      );

      const suggestion = res?.data?.suggestion;

      if (suggestion) {
        setRowPatch(idx, {
          aiLoading: false,
          aiSuggestion: suggestion,
          // اگر دوست داری، نمره‌ی دستی را هم هم‌زمان ست کن:
          manualScore: suggestion.score,
        });
      } else {
        setRowPatch(idx, {
          aiLoading: false,
          aiError: "No suggestion from AI",
        });
      }
    } catch (e) {
      setRowPatch(idx, {
        aiLoading: false,
        aiError: e?.message || "AI grading failed",
      });
    }
  };

  // ✅ نمایش پاسخ کاربر بسته به نوع سؤال
  const renderUserAnswer = (r) => {
    // speaking/audio: فایل‌های آپلودی در attachments
    if (r.itemKind === "speaking_integrated" || r.itemKind === "audio_prompt") {
      if (Array.isArray(r.attachments) && r.attachments.length) {
        return (
          <div className="space-y-2">
            {r.attachments.map((url, i) => (
              <audio
                key={i}
                src={CDN_BASE_URL + url}
                controls
                className="w-full"
              />
            ))}
          </div>
        );
      }
      // اگر به دلایلی attachment نبود، شاید value.recording.url باشد
      const recUrl = r?.value?.recording?.url;
      if (recUrl) return <audio src={recUrl} controls className="w-full" />;
      return (
        <div className="text-xs text-gray-500">No recording provided.</div>
      );
    }

    if (r.itemKind === "file_upload") {
      // ۱) لینک‌ها از attachments (برای سازگاری با نسخه‌های قدیمی)
      const links = [];

      if (Array.isArray(r.attachments) && r.attachments.length) {
        r.attachments.forEach((url, i) => {
          if (!url) return;
          links.push({
            url: typeof url === "string" ? url : String(url),
            label: `Attachment ${i + 1}`,
          });
        });
      }

      // ۲) لینک از value.file / value.files (ساختار جدید)
      const v = r.value;

      if (v) {
        // حالت: { file: {...} }
        if (v.file && typeof v.file === "object") {
          const f = v.file;
          if (f.url) {
            links.push({
              url: f.url,
              label: f.name || "Uploaded file",
              size: f.size,
            });
          }
        }

        // حالت احتمالی: { files: [ {...}, {...} ] }
        if (Array.isArray(v.files)) {
          v.files.forEach((f, idx) => {
            if (!f || typeof f !== "object" || !f.url) return;
            links.push({
              url: f.url,
              label: f.name || `File ${idx + 1}`,
              size: f.size,
            });
          });
        }

        // حالت خیلی ساده: value خودش رشته URL باشد
        if (typeof v === "string") {
          links.push({
            url: v,
            label: "Uploaded file",
          });
        }
      }

      if (!links.length) {
        return <div className="text-xs text-gray-500">No file uploaded.</div>;
      }

      const formatSize = (bytes) => {
        if (!bytes || typeof bytes !== "number") return "";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      };

      return (
        <ul className="list-disc ps-5">
          {links.map((f, i) => (
            <li key={i}>
              <a
                className="text-indigo-600 underline"
                href={CDN_BASE_URL + f.url}
                target="_blank"
                rel="noreferrer"
              >
                {f.label}
                {f.size ? ` (${formatSize(f.size)})` : ""}
              </a>
            </li>
          ))}
        </ul>
      );
    }

    if (
      r.itemKind === "essay" ||
      r.itemKind === "writing_prompt" ||
      r.itemKind === "short_text"
    ) {
      const text = r?.value?.text || r?.value?.answer || r?.value || "";
      return text ? (
        <div className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 rounded-md border p-3">
          {String(text)}
        </div>
      ) : (
        <div className="text-xs text-gray-500">No answer.</div>
      );
    }

    // سایر انواع (در صورت نیاز بعداً جزئی‌تر کنید)
    // مثال: mcq_single → value.selected
    if (r.itemKind === "mcq_single") {
      const sel = r?.value?.selected;
      return (
        <div className="text-sm">
          <span className="text-gray-500">Selected: </span>
          <b>{sel ?? "—"}</b>
        </div>
      );
    }
    if (r.itemKind === "mcq_multi") {
      const arr = Array.isArray(r?.value?.selected) ? r.value.selected : [];
      return (
        <div className="text-sm">
          <span className="text-gray-500">Selected: </span>
          <b>{arr.length ? arr.join(", ") : "—"}</b>
        </div>
      );
    }

    // fallback: نمایش JSON
    return (
      <pre className="text-[12px] bg-gray-50 border rounded p-2 overflow-auto">
        {JSON.stringify(r.value ?? null, null, 2)}
      </pre>
    );
  };

  const isRtl = i18n.language.includes("fa");
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  const reviewCount =
    head?.responses?.filter((r) => r.needsReview === true).length ?? 0;

  if (!head) return <div className="p-4 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_results.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs text-gray-500">{head.userName}</div>
              <h1 className="text-xl font-semibold">{head.examTitle}</h1>
              <div className="text-xs text-gray-400">
                {reviewCount} item(s) to grade
              </div>
            </div>
            <div className="text-sm">
              <div>
                Auto: <b>{head.totals.auto}</b>
              </div>
              <div>
                Manual: <b>{head.totals.manual}</b>
              </div>
              <div>
                Total: <b>{head.totals.score}</b>
              </div>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-lg border bg-white p-4 text-sm text-gray-500">
              Nothing to grade here.
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((r, idx) => {
                const isLocked = r.needsReview === false;
                return (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    <div className="text-xs text-gray-500 mb-1">
                      #{r.index + 1} · {r.itemKind} · {r.itemPoints} pts
                    </div>

                    {/* متن سوال (اول انگلیسی بعد فارسی) */}
                    {r.stem?.en || r.stem?.fa ? (
                      <div
                        className="prose max-w-none mb-3"
                        dangerouslySetInnerHTML={{
                          __html: r.stem?.en || r.stem?.fa,
                        }}
                      />
                    ) : null}

                    {/* پاسخ کاربر */}
                    <div className="mb-3">
                      <div className="text-xs text-gray-500 mb-1">
                        User answer
                      </div>
                      {renderUserAnswer(r)}
                    </div>

                    {!isLocked && (
                      <div className="mb-3">
                        <button
                          onClick={() => gradeRowWithAI(idx, r)}
                          disabled={r.aiLoading}
                          className={`px-3 py-1.5 rounded text-sm border ${
                            r.aiLoading
                              ? "bg-gray-100 text-gray-400 cursor-wait"
                              : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                          }`}
                        >
                          {r.aiLoading
                            ? "در حال تصحیح با AI..."
                            : "تصحیح با هوش مصنوعی"}
                        </button>
                      </div>
                    )}

                    {r.aiSuggestion && (
                      <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2 leading-5">
                        <div className="font-semibold mb-1">
                          پیشنهاد AI: {r.aiSuggestion.score} /{" "}
                          {r.aiSuggestion.maxScore} ({r.aiSuggestion.judgement})
                        </div>
                        {r.aiSuggestion.explanation && (
                          <div className="text-gray-700 whitespace-pre-line">
                            {r.aiSuggestion.explanation}
                          </div>
                        )}
                        <div className="mt-1 text-[11px] text-gray-500">
                          مصحح می‌تواند این نمره را تغییر دهد.
                        </div>
                      </div>
                    )}

                    {r.aiError && (
                      <div className="mb-3 text-xs text-rose-600">
                        خطا در تصحیح با AI: {r.aiError}
                      </div>
                    )}

                    {/* فرم نمره و کامنت */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500">
                          Auto score
                        </label>
                        <div className="font-mono">
                          {r.autoScore ?? 0} / {r.itemPoints}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Manual score
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={r.itemPoints}
                          value={r.manualScore ?? 0}
                          onChange={(e) =>
                            setRowPatch(idx, {
                              manualScore: Number(e.target.value),
                            })
                          }
                          disabled={isLocked}
                          className={`w-28 border rounded px-2 py-1 ${
                            isLocked
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : ""
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Comment
                        </label>
                        <input
                          type="text"
                          value={r.comment || ""}
                          onChange={(e) =>
                            setRowPatch(idx, { comment: e.target.value })
                          }
                          disabled={isLocked}
                          className={`w-full border rounded px-2 py-1 ${
                            isLocked
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : ""
                          }`}
                          placeholder="Optional feedback…"
                        />
                      </div>
                    </div>
                    {isLocked && (
                      <div className="mt-1 text-[11px] text-gray-400">
                        این سؤال به‌صورت خودکار تصحیح شده و قابل‌تغییر نیست.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1.5 rounded border"
            >
              Back
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-white ${
                saving ? "bg-gray-400" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {saving ? "Saving…" : "Save grades"}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
